import { Flex } from '@chakra-ui/react';
import React, { useMemo, useCallback } from 'react';

import { DataPipeline } from '@finley-technologies/finley-types';

import { SearchSelect } from 'packages/common-frontend/components/SearchSelect';

// MeshSelect component allows users to select a specific mesh document from a list of available meshes.
// It also allows a special "All Meshes option" to be selected if caller allows it.
export const MeshSelect = ({
  onMeshSelected,
  meshDocuments,
  selectedMeshDocumentId,
  allowSelection,
  allowAllOption = false,
}: {
  allowSelection: boolean;
  selectedMeshDocumentId: string | null;
  allowAllOption?: boolean; // if true, we allow selecting "All Meshes" option
  meshDocuments: DataPipeline.MeshDocumentMetadata[];

  // if allowSelection is true, this callback is called with the selected mesh document
  // if user selects "All Meshes" option, this callback is called with null
  onMeshSelected: (
    meshDocument: DataPipeline.MeshDocumentMetadata | null,
  ) => void;
}) => {
  const meshOptions = useMemo(() => {
    const options = meshDocuments
      .map((meshDocument) => ({
        id: meshDocument.meshDocumentId,
        meshName: meshDocument.meshName,
      }))
      .filter(({ id }) => allowSelection || id === selectedMeshDocumentId)
      .sort((a, b) => a.meshName.localeCompare(b.meshName))
      .map(({ id, meshName }) => ({
        value: id,
        label: meshName,
      }));

    if (allowSelection && allowAllOption && options.length > 1) {
      // add an "All Facilities" option
      options.unshift({
        value: 'all_all',
        label: 'All Facilities',
      });
    }

    // if we are given the selected mesh document id,
    // look for corresponding option
    const selected = selectedMeshDocumentId
      ? options.find((m) => m.value === selectedMeshDocumentId)
      : options[0];

    return { options, selected };
  }, [allowAllOption, allowSelection, meshDocuments, selectedMeshDocumentId]);

  const selectDisabled = !allowSelection || meshOptions.options.length <= 1;

  const handleChange = useCallback(
    (value?: string) => {
      if (value) {
        // handle "All Facilities" option
        if (value === 'all_all') {
          onMeshSelected(null);
          return;
        }

        const selectedMeshDocument = meshDocuments.find(
          (m) => m.meshDocumentId === value,
        );
        if (selectedMeshDocument) {
          onMeshSelected(selectedMeshDocument);
        }
      }
    },
    [meshDocuments, onMeshSelected],
  );

  return (
    <Flex height="100%" alignItems="center" width="100%">
      {meshOptions.options.length > 0 && (
        <SearchSelect
          value={meshOptions.selected}
          options={meshOptions.options}
          onChange={handleChange}
          isDisabled={selectDisabled}
        />
      )}
    </Flex>
  );
};
